import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CalendarEventWithRelations, CalendarEventComment } from '../types/calendar';
import { calendarApiService } from '../services/calendarApi';
import { Evidence } from '../types/player';
import { notesApiService } from '../services/notesApi';
import { elasticsearchService } from '../services/elasticsearchService';
import { usernameCacheService } from '../services/usernameCache';
import { MarkdownRenderer } from './MarkdownRenderer';
import { 
  X,
  Trash2, 
  MessageSquare, 
  Loader,
  File,
  Users,
  Plus,
  Unlink,
  Upload,
  Eye,
  Tag as TagIcon,
  Calendar as CalendarIcon,
  Search,
  Copy,
  Check,
  ChevronDown,
  Globe,
  Lock,
  Link2,
  PartyPopper,
  Briefcase,
  Hammer,
  Handshake,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../utils/cn';
import { useAuth } from '../contexts/AuthContext';
import { CreatorProfile } from './CreatorProfile';
import { PlayerProfile } from './PlayerProfile';
import { ConfirmationModal } from './ConfirmationModal';
import { ConfidentialEvidencePlaceholder } from './ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from './ConfidentialBadge';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';
import { useDebounce } from '../hooks/useDebounce';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { KanbanLinkedCardsSection } from './kanban/KanbanLinkedCardsSection';
import { useNavigate } from 'react-router-dom';
import {
  calendarLinkResolver,
  CALENDAR_LINK_SOURCE_LABELS,
  type CalendarLinkTarget,
} from '../services/calendarLinkSources';
import { getCalendarLinkSources } from '../utils/calendarLinkAccess';

const LINK_TARGET_ICONS: Record<string, { icon: typeof Link2; className: string }> = {
  Event: { icon: PartyPopper, className: 'text-accent-400' },
  'Event project': { icon: Briefcase, className: 'text-blue-400' },
  'Build project': { icon: Hammer, className: 'text-amber-400' },
  Build: { icon: Hammer, className: 'text-amber-400' },
  Partnership: { icon: Handshake, className: 'text-emerald-400' },
};

interface CalendarDetailModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

interface PlayerSearchResult {
  _id: string;
  _source: {
    uuid: string;
    lastKnownName: string;
    nickname: string;
    lastKnownIp: string;
  };
}

export const CalendarDetailModal = ({ eventId, isOpen, onClose, onUpdate }: CalendarDetailModalProps) => {
  const { identity } = useAuth();
  const [event, setEvent] = useState<CalendarEventWithRelations | null>(null);
  const [comments, setComments] = useState<CalendarEventComment[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUserLinkModalOpen, setIsUserLinkModalOpen] = useState(false);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [uploadConfidential, setUploadConfidential] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Evidence | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<PlayerSearchResult[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [isLinkingUser, setIsLinkingUser] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [isAddingTag, setIsAddingTag] = useState<string | null>(null);
  const [startTimeMenuOpen, setStartTimeMenuOpen] = useState(false);
  const [endTimeMenuOpen, setEndTimeMenuOpen] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState<string | null>(null);
  // Team-module records (events, builds, partnerships) that link this event.
  const [linkTargets, setLinkTargets] = useState<CalendarLinkTarget[]>([]);
  const navigate = useNavigate();
  const isDev = import.meta.env.DEV;
  
  const debouncedUserSearch = useDebounce(userSearchQuery, 300);
  
  const startTimeMenuRef = useRef<HTMLDivElement>(null);
  const endTimeMenuRef = useRef<HTMLDivElement>(null);
  
  useOnClickOutside(startTimeMenuRef, () => setStartTimeMenuOpen(false));
  useOnClickOutside(endTimeMenuRef, () => setEndTimeMenuOpen(false));

  const canManage = useMemo(() => {
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.edit');
  }, [identity]);

  const canDelete = useMemo(() => {
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.delete');
  }, [identity]);

  const canCancel = useMemo(() => {
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.cancel');
  }, [identity]);

  const canPostpone = useMemo(() => {
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.postpone');
  }, [identity]);

  const permissions = useMemo(() => identity?.raw.custom.permissions || [], [identity]);

  useEffect(() => {
    if (!isOpen || !eventId) return;
    setLinkTargets([]);
    const sources = getCalendarLinkSources(identity, isDev);
    if (sources.length === 0) return;
    let cancelled = false;
    calendarLinkResolver
      .resolve(eventId, sources)
      .then(summary => {
        if (!cancelled) setLinkTargets(summary.targets);
      })
      .catch(() => {
        if (!cancelled) setLinkTargets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId, identity, isDev]);

  // Backend already filters tags by permissions, so all tags from events are accessible
  // We still check permissions for tooltip display purposes
  const visibleTags = useMemo(() => {
    if (!event) return [];
    return event.tags;
  }, [event]);

  useEffect(() => {
    if (isOpen && eventId) {
      fetchData();
    }
  }, [isOpen, eventId]);

  useEffect(() => {
    if (isUserLinkModalOpen && debouncedUserSearch.length >= 2) {
      searchUsers();
    } else if (debouncedUserSearch.length < 2) {
      setUserSearchResults([]);
    }
  }, [debouncedUserSearch, isUserLinkModalOpen]);

  const searchUsers = async () => {
    setIsSearchingUsers(true);
    try {
      const searchResults = await elasticsearchService.searchPlayers(debouncedUserSearch);
      setUserSearchResults(searchResults.hits.hits);
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleLinkUser = async (userUuid: string) => {
    if (!eventId) return;
    
    setIsLinkingUser(userUuid);
    try {
      await calendarApiService.addUserToEvent(eventId, userUuid);
      fetchData();
      setUserSearchQuery('');
      setUserSearchResults([]);
      setIsUserLinkModalOpen(false);
    } catch (err) {
      alert('Failed to link user');
    } finally {
      setIsLinkingUser(null);
    }
  };

  const handleUsernamesUpdated = useCallback(() => {
    userSearchResults.forEach(result => {
      if (!result._source.lastKnownName) {
        const newName = usernameCacheService.getName(result._source.uuid);
        if (newName) {
          elasticsearchService.updatePlayerName(result._id, newName)
            .catch(err => console.error(`Failed to update name for ${result._source.uuid}:`, err));
        }
      }
    });
  }, [userSearchResults]);

  useEffect(() => {
    if (userSearchResults.length > 0) {
      const uuids = userSearchResults.map((hit: any) => hit._source.uuid);
      usernameCacheService.prefetchUsernames(uuids);
    }
    
    document.addEventListener('usernamesUpdated', handleUsernamesUpdated);
    return () => {
      document.removeEventListener('usernamesUpdated', handleUsernamesUpdated);
    };
  }, [userSearchResults, handleUsernamesUpdated]);

  const fetchData = async () => {
    if (!eventId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [eventData, commentsData, evidenceData, tagsData] = await Promise.all([
        calendarApiService.getEventById(eventId),
        calendarApiService.getComments(eventId),
        notesApiService.listEvidenceForItem(eventId), // Uses existing evidence API
        calendarApiService.getAllTags(),
      ]);
      
      setEvent(eventData);
      setComments(commentsData);
      setEvidence(evidenceData);
      setAllTags(tagsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (!eventId || !event) return;
    
    setIsAddingTag(tagId);
    try {
      await calendarApiService.addTagToEvent(eventId, tagId);
      // Check if we're adding a restricted tag to a published event
      const tag = allTags.find(t => t.id === tagId);
      if (tag?.visibility === 'restricted' && event.publish) {
        // Automatically unpublish when restricted tag is added
        await calendarApiService.updateEvent(eventId, { publish: false });
      }
      fetchData();
      onUpdate?.();
    } catch (err) {
      alert('Failed to add tag');
    } finally {
      setIsAddingTag(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!eventId) return;
    
    try {
      await calendarApiService.removeTagFromEvent(eventId, tagId);
      fetchData();
    } catch (err) {
      alert('Failed to remove tag');
    }
  };

  const handleTogglePublish = async () => {
    if (!eventId || !event) return;
    
    // Check if event has restricted tags
    const hasRestrictedTags = event.tags.some(tag => tag.visibility === 'restricted');
    if (hasRestrictedTags && !event.publish) {
      alert('Cannot publish events with restricted tags. Remove restricted tags first.');
      return;
    }
    
    try {
      await calendarApiService.updateEvent(eventId, {
        publish: !event.publish
      });
      fetchData();
      onUpdate?.();
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  const getDiscordTimestamp = (timestamp: number, format: string) => {
    const unixSeconds = Math.floor(timestamp / 1000);
    return `<t:${unixSeconds}:${format}>`;
  };

  const copyDiscordTimestamp = async (timestamp: number, format: string, label: string) => {
    const timestampText = getDiscordTimestamp(timestamp, format);
    await navigator.clipboard.writeText(timestampText);
    setCopiedFormat(`${label}-${format}`);
    setTimeout(() => setCopiedFormat(null), 2000);
  };

  const timestampFormats = [
    { code: 'R', label: 'Relative', desc: 'in 2 hours' },
    { code: 'F', label: 'Long Date & Time', desc: 'Monday, January 1, 2024 12:00 PM' },
    { code: 'f', label: 'Short Date & Time', desc: 'January 1, 2024 12:00 PM' },
    { code: 'D', label: 'Short Date', desc: '01/01/2024' },
    { code: 'd', label: 'Short Date', desc: 'Jan 1, 2024' },
    { code: 't', label: 'Short Time', desc: '12:00 PM' },
    { code: 'T', label: 'Long Time', desc: '12:00:00 PM' },
  ];

  const handleAddComment = async () => {
    if (!newComment.trim() || !eventId) return;
    
    setIsSubmittingComment(true);
    try {
      const comment = await calendarApiService.addComment(eventId, newComment);
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (err) {
      alert('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!eventId) return;
    
    setIsDeleting(true);
    try {
      await calendarApiService.deleteEvent(eventId);
      onUpdate?.();
      onClose();
    } catch (err) {
      alert('Failed to delete event');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUnlinkUser = async (userUuid: string) => {
    if (!eventId) return;
    
    try {
      await calendarApiService.removeUserFromEvent(eventId, userUuid);
      setEvent(prev => prev ? {
        ...prev,
        users: prev.users.filter(u => u.user_uuid !== userUuid)
      } : null);
    } catch (err) {
      alert('Failed to unlink user');
    }
  };

  const handleUploadEvidence = async (file: File) => {
    if (!eventId) return;
    
    setIsUploadingEvidence(true);
    try {
      await notesApiService.uploadEvidence(file, eventId, 'calendar_event', uploadConfidential || undefined);
      fetchData();
    } catch (err) {
      alert('Failed to upload evidence');
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const handleUnlinkEvidence = async (evidenceId: string) => {
    if (!eventId) return;
    
    try {
      await notesApiService.deleteEvidence(evidenceId);
      setEvidence(prev => prev.filter(e => e.id !== evidenceId));
    } catch (err) {
      alert('Failed to unlink evidence');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/15 text-green-300';
      case 'cancelled': return 'bg-red-500/15 text-red-300';
      case 'postponed': return 'bg-yellow-500/15 text-yellow-300';
      default: return 'bg-gray-500/15 text-gray-300';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
            className="modal-overlay z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setStartTimeMenuOpen(false);
            setEndTimeMenuOpen(false);
            onClose();
          }
        }}
      >
        <motion.div
          className="modal-panel overflow-hidden max-w-5xl"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.3 }}
        >
          {/* Header */}
          <div className="p-6 border-b border-glass-200/20">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                {canCancel && (
                  <button onClick={async () => {
                    if (!eventId) return;
                    try {
                      await calendarApiService.cancelEvent(eventId);
                      fetchData();
                      onUpdate?.();
                    } catch (err) {
                      alert('Failed to cancel event');
                    }
                  }} className="glass-button-secondary text-yellow-400 hover:bg-yellow-500/15">
                    Cancel Event
                  </button>
                )}
                {canPostpone && (
                  <button onClick={async () => {
                    if (!eventId) return;
                    try {
                      await calendarApiService.postponeEvent(eventId);
                      fetchData();
                      onUpdate?.();
                    } catch (err) {
                      alert('Failed to postpone event');
                    }
                  }} className="glass-button-secondary text-orange-400 hover:bg-orange-500/15">
                    Postpone Event
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => setShowDeleteConfirm(true)} className="glass-button-secondary text-red-400 hover:bg-red-500/15">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
              <button onClick={onClose} className="glass-button-secondary">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-white mb-2 flex items-center">
                  <CalendarIcon className="w-8 h-8 mr-3 text-accent-400" />
                  {event?.title || 'Loading...'}
                </h1>
                {event && (
                  <div className="flex items-center space-x-4 text-sm text-glass-400">
                    <span>ID: <span className="font-mono">{event.id}</span></span>
                    <span>Created {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}</span>
                    <span>By <CreatorProfile creatorId={event.created_by} inline /></span>
                    {event.updated_at && (
                      <span>Updated {formatDistanceToNow(new Date(event.updated_at), { addSuffix: true })}</span>
                    )}
                  </div>
                )}
              </div>
              {event && (
                <div className="flex items-center space-x-2">
                  {event.publish && (
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/50 flex items-center space-x-1">
                      <Globe className="w-3 h-3" />
                      <span>Published</span>
                    </span>
                  )}
                  <span className={cn(
                    "text-xs font-bold px-3 py-1 rounded-full",
                    getStatusColor(event.status)
                  )}>
                    {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Content - Sidebar Layout */}
          <div className="flex-1 overflow-hidden flex">
            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-accent-400" />
              </div>
            )}

            {error && (
              <div className="glass-card p-4 border-l-4 border-red-400">
                <div className="flex items-center space-x-2">
                  <span className="text-red-300">{error}</span>
                </div>
              </div>
            )}

            {event && (
              <>
                {/* Event Details */}
                <div className="glass-card p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Event Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-glass-400 text-sm">Start Time:</span>
                        <div className="relative" ref={startTimeMenuRef}>
                          <button
                            onClick={() => {
                              setStartTimeMenuOpen(!startTimeMenuOpen);
                              setEndTimeMenuOpen(false);
                            }}
                            className="glass-button-secondary text-xs px-2 py-1 flex items-center space-x-1"
                          >
                            <Copy className="w-3 h-3" />
                            <span>Copy Timestamp</span>
                            <ChevronDown className={cn("w-3 h-3 transition-transform", startTimeMenuOpen && "rotate-180")} />
                          </button>
                          {startTimeMenuOpen && (
                            <div className="absolute right-0 top-full mt-1 bg-dark-800 border border-glass-200/20 rounded-lg shadow-xl z-[60] min-w-[200px]">
                              <div className="p-2 space-y-1">
                                {timestampFormats.map((format) => (
                                  <button
                                    key={format.code}
                                    onClick={() => {
                                      copyDiscordTimestamp(event.start_time, format.code, 'start');
                                      setStartTimeMenuOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 rounded hover:bg-glass-200/10 text-sm text-white transition-colors"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium">{format.label}</div>
                                        <div className="text-xs text-glass-400">{format.desc}</div>
                                      </div>
                                      {copiedFormat === `start-${format.code}` ? (
                                        <Check className="w-4 h-4 text-green-400" />
                                      ) : (
                                        <Copy className="w-3 h-3 text-glass-400" />
                                      )}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-white font-mono">{new Date(event.start_time).toLocaleString()}</p>
                    </div>
                    {event.end_time && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-glass-400 text-sm">End Time:</span>
                          <div className="relative" ref={endTimeMenuRef}>
                            <button
                              onClick={() => {
                                setEndTimeMenuOpen(!endTimeMenuOpen);
                                setStartTimeMenuOpen(false);
                              }}
                              className="glass-button-secondary text-xs px-2 py-1 flex items-center space-x-1"
                            >
                              <Copy className="w-3 h-3" />
                              <span>Copy Timestamp</span>
                              <ChevronDown className={cn("w-3 h-3 transition-transform", endTimeMenuOpen && "rotate-180")} />
                            </button>
                            {endTimeMenuOpen && (
                              <div className="absolute right-0 top-full mt-1 bg-dark-800 border border-glass-200/20 rounded-lg shadow-xl z-[60] min-w-[200px]">
                                <div className="p-2 space-y-1">
                                  {timestampFormats.map((format) => (
                                    <button
                                      key={format.code}
                                      onClick={() => {
                                        copyDiscordTimestamp(event.end_time!, format.code, 'end');
                                        setEndTimeMenuOpen(false);
                                      }}
                                      className="w-full text-left px-3 py-2 rounded hover:bg-glass-200/10 text-sm text-white transition-colors"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="font-medium">{format.label}</div>
                                          <div className="text-xs text-glass-400">{format.desc}</div>
                                        </div>
                                        {copiedFormat === `end-${format.code}` ? (
                                          <Check className="w-4 h-4 text-green-400" />
                                        ) : (
                                          <Copy className="w-3 h-3 text-glass-400" />
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-white font-mono">{new Date(event.end_time).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="glass-card p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Description</h2>
                  <MarkdownRenderer content={event.description} />
                </div>

                {/* Comments */}
                <div className="glass-card p-6">
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Comments ({comments.length})
                  </h2>

                  {/* Add Comment */}
                  {canManage && (
                    <div className="mb-6">
                      <MarkdownEditor
                        value={newComment}
                        onChange={setNewComment}
                        height={200}
                        placeholder="Enter comment in markdown..."
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || isSubmittingComment}
                        className="glass-button mt-3"
                      >
                        {isSubmittingComment ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>
                  )}

                  {/* Comments List */}
                  <div className="space-y-4">
                    {comments.length === 0 ? (
                      <p className="text-glass-400">No comments yet.</p>
                    ) : (
                      [...comments].reverse().map((comment) => (
                        <motion.div
                          key={comment.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="glass-card p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <CreatorProfile creatorId={comment.author_id} inline />
                              <span className="text-xs text-glass-400">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          <MarkdownRenderer content={comment.content} />
                        </motion.div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
            </div>

            {/* Sidebar */}
            <div className="w-80 border-l border-glass-200/20 overflow-y-auto bg-glass-100/5">
              <div className="p-4 space-y-4">
                {/* Tags */}
                <div className="glass-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-white flex items-center">
                      <TagIcon className="w-4 h-4 mr-2" />
                      Tags ({visibleTags.length})
                    </h3>
                    {canManage && (
                      <button onClick={() => setIsTagModalOpen(true)} className="glass-button-secondary p-1">
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {visibleTags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {visibleTags.map((tag) => {
                        const isCreatorWithoutPermission = tag.visibility === 'restricted' && 
                                                          identity?.user_uuid === tag.created_by && 
                                                          !permissions.includes(`argus.calendar.tag.${tag.name}`);
                        
                        return (
                          <div key={tag.id} className="flex items-center space-x-1 group relative">
                            <span
                              className="px-3 py-1 rounded-full text-sm text-white"
                              style={{ backgroundColor: tag.color }}
                            >
                              {tag.name}
                            </span>
                            {canManage && (
                              <button
                                onClick={() => handleRemoveTag(tag.id)}
                                className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove tag"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                            {isCreatorWithoutPermission && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-dark-800 border border-glass-200/20 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                                Request permission: <code className="bg-dark-900 px-1 py-0.5 rounded">argus.calendar.tag.{tag.name}</code>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                  <div className="w-2 h-2 bg-dark-800 border-l border-b border-glass-200/20 rotate-45"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-sm">No tags assigned.</p>
                  )}
                </div>

                {/* Linked from team modules */}
                {linkTargets.length > 0 && (
                  <div className="glass-card p-4">
                    <h3 className="text-sm font-semibold text-white flex items-center mb-1">
                      <Link2 className="w-4 h-4 mr-2" />
                      Linked from ({linkTargets.length})
                    </h3>
                    <p className="text-xs text-glass-400 mb-3">
                      Team records that put this event on the calendar, where it is drawn as an outline.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {linkTargets.map((target) => {
                        const iconConfig = LINK_TARGET_ICONS[target.kind] ?? { icon: Link2, className: 'text-glass-400' };
                        const Icon = iconConfig.icon;
                        return (
                          <button
                            key={`${target.source}-${target.href}`}
                            type="button"
                            onClick={() => {
                              onClose();
                              navigate(target.href);
                            }}
                            className="chip hover:text-white hover:border-glass-200/40 transition-colors max-w-full"
                            title={`Open in ${CALENDAR_LINK_SOURCE_LABELS[target.source]}`}
                          >
                            <Icon className={cn('w-3 h-3 shrink-0', iconConfig.className)} />
                            <span className="truncate">{target.label}</span>
                            <span className="text-glass-500 shrink-0">· {CALENDAR_LINK_SOURCE_LABELS[target.source]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Publish Status */}
                {canManage && event && (
                  <div className="glass-card p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="publish-toggle"
                        checked={event.publish}
                        onChange={handleTogglePublish}
                        disabled={event.tags.some(tag => tag.visibility === 'restricted')}
                        className="mt-1 w-4 h-4 rounded border-glass-300 bg-dark-800 text-accent-400 focus:ring-accent-400 focus:ring-2"
                      />
                      <div className="flex-1">
                        <label htmlFor="publish-toggle" className="flex items-center space-x-2 cursor-pointer">
                          <Globe className="w-4 h-4 text-accent-400" />
                          <span className="text-sm font-medium text-white">Publish to Public Calendar</span>
                        </label>
                        <p className="text-xs text-glass-400 mt-1">
                          {event.tags.some(tag => tag.visibility === 'restricted')
                            ? 'Cannot publish events with restricted tags. Remove restricted tags to enable publishing.'
                            : event.publish
                            ? 'This event is visible on the public community calendar.'
                            : 'Make this event visible on the public community calendar for all members to see.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Linked Kanban Cards */}
                <KanbanLinkedCardsSection linkType="calendar_event" targetId={eventId} title="Linked Kanban Cards" />

                {/* Assigned Users */}
                <div className="glass-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-white flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      Users ({event?.users.length || 0})
                    </h3>
                    {canManage && (
                      <button onClick={() => setIsUserLinkModalOpen(true)} className="glass-button-secondary p-1">
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {event && event.users.length > 0 ? (
                    <div className="space-y-2">
                      {event.users.map((user) => (
                        <div key={user.user_uuid} className="glass-card p-2 flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <PlayerProfile uuid={user.user_uuid} showUuid={false} showAvatar={true} inline={true} />
                          </div>
                          {canManage && (
                            <button
                              onClick={() => handleUnlinkUser(user.user_uuid)}
                              className="text-red-400 hover:text-red-300 flex-shrink-0"
                              title="Unlink"
                            >
                              <Unlink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-sm">No users assigned.</p>
                  )}
                </div>

                {/* Media */}
                <div className="glass-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-white flex items-center">
                      <File className="w-4 h-4 mr-2" />
                      Media ({evidence.length})
                    </h3>
                    {canManage && (
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => setUploadConfidential(!uploadConfidential)}
                          className={cn(
                            "glass-button-secondary p-1",
                            uploadConfidential && "bg-amber-500/15 text-amber-300 border-amber-500/25"
                          )}
                          title={uploadConfidential ? 'Uploads will be confidential' : 'Uploads will be public'}
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                        <label className="glass-button-secondary p-1 cursor-pointer">
                          <Upload className="w-4 h-4" />
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadEvidence(file);
                            }}
                            disabled={isUploadingEvidence}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  {evidence.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {evidence.map((item) => {
                        const redacted = isEvidenceRedacted(item, identity);
                        return (
                        <div key={item.id} className="relative group" title={item.filename}>
                          {redacted ? (
                            <div className="w-full aspect-square rounded-md overflow-hidden">
                              <ConfidentialEvidencePlaceholder compact />
                            </div>
                          ) : item.content_type.startsWith('image/') ? (
                            <img
                              src={getEvidenceViewUrl(item.id)}
                              alt={item.filename}
                              className="w-full aspect-square object-cover rounded-md cursor-pointer"
                              onClick={() => setSelectedImage(item)}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full aspect-square bg-glass-200/10 rounded-md flex items-center justify-center cursor-pointer" onClick={() => window.open(getEvidenceViewUrl(item.id), '_blank')}>
                              <File className="w-8 h-8 text-glass-400" />
                            </div>
                          )}
                          {!redacted && (
                            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2 rounded-md">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.content_type.startsWith('image/')) {
                                    setSelectedImage(item);
                                  } else {
                                    window.open(getEvidenceViewUrl(item.id), '_blank');
                                  }
                                }}
                                className="p-2 rounded-full bg-glass-200/20 hover:bg-blue-500/50 text-white"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {canManage && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnlinkEvidence(item.id);
                                  }}
                                  className="p-2 rounded-full bg-glass-200/20 hover:bg-red-500/50 text-white"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                          {item.confidential && !redacted && (
                            <div className="absolute top-1 right-1">
                              <ConfidentialBadge />
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-glass-400 text-sm">No media uploaded.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Image Lightbox */}
      {selectedImage && !isEvidenceRedacted(selectedImage, identity) && (
        <div className="modal-overlay z-[60]" onClick={() => setSelectedImage(null)}>
          <img
            src={getEvidenceViewUrl(selectedImage.id)}
            alt={selectedImage.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setSelectedImage(null)}>
            <X className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Modals */}
      {showDeleteConfirm && (
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Event"
          message="Are you sure you want to delete this event? This action cannot be undone."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}

      {/* User Link Modal */}
      {isUserLinkModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay z-[60]"
            onClick={() => setIsUserLinkModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="modal-panel max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Users className="w-6 h-6 text-accent-400" />
                  <h2 className="text-lg font-semibold tracking-tight text-white">Link User to Event</h2>
                </div>
                <button onClick={() => setIsUserLinkModalOpen(false)} className="glass-button p-2 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="glass-input w-full pl-10"
                  placeholder="Search by name, UUID, or IP..."
                />
                {isSearchingUsers && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400 animate-spin" />}
              </div>

              {userSearchQuery.length > 0 && userSearchQuery.length < 2 && (
                <div className="text-center py-8 text-glass-400">
                  Enter at least 2 characters to search
                </div>
              )}

              {userSearchResults.length === 0 && userSearchQuery.length >= 2 && !isSearchingUsers && (
                <div className="text-center py-8 text-glass-400">
                  No players found
                </div>
              )}

              {userSearchResults.length > 0 && (
                <div className="space-y-2">
                  {userSearchResults.map((result) => (
                    <div
                      key={result._id}
                      className="glass-card p-4 flex items-center justify-between hover:bg-glass-200/10 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <PlayerProfile
                          uuid={result._source.uuid}
                          name={result._source.lastKnownName}
                          nickname={result._source.nickname}
                          showUuid={true}
                        />
                      </div>
                      <button
                        onClick={() => handleLinkUser(result._source.uuid)}
                        disabled={isLinkingUser === result._source.uuid}
                        className="glass-button text-sm ml-4 whitespace-nowrap"
                      >
                        {isLinkingUser === result._source.uuid ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Linking...
                          </>
                        ) : (
                          'Link User'
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Tag Link Modal */}
      {isTagModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay z-[60]"
            onClick={() => setIsTagModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="modal-panel max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <TagIcon className="w-6 h-6 text-accent-400" />
                  <h2 className="text-lg font-semibold tracking-tight text-white">Add Tag to Event</h2>
                </div>
                <button onClick={() => setIsTagModalOpen(false)} className="glass-button p-2 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {allTags.length === 0 ? (
                <div className="text-center py-8 text-glass-400">
                  No tags available
                </div>
              ) : (
                              <div className="space-y-2">
                                {allTags
                                  .filter(tag => !event?.tags.find(et => et.id === tag.id))
                                  .map((tag) => (
                                    <div
                                      key={tag.id}
                                      className="glass-card p-4 flex items-center justify-between hover:bg-glass-200/10 transition-colors"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div
                                          className="w-4 h-4 rounded-full"
                                          style={{ backgroundColor: tag.color }}
                                        />
                                        <span className="text-white">{tag.name}</span>
                                      </div>
                                      <button
                                        onClick={() => handleAddTag(tag.id)}
                                        disabled={isAddingTag === tag.id}
                                        className="glass-button text-sm"
                                      >
                                        {isAddingTag === tag.id ? (
                                          <>
                                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                                            Adding...
                                          </>
                                        ) : (
                                          'Add Tag'
                                        )}
                                      </button>
                                    </div>
                                  ))}
                              </div>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </AnimatePresence>
  );
};

